import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { 
    Segment, 
    Header, 
    Table, 
    Checkbox, 
    Button, 
    Icon, 
    Message,
    Loader,
    Dimmer,
    Grid,
    Divider,
    Label
} from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';

const NotificationPreferences = observer(() => {
    const { notificationStore } = useStore();
    const { t } = useTranslation();
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    useEffect(() => {
        notificationStore.loadPreferences();
    }, [notificationStore]);

    const toggleCategory = (category: string) => {
        const newExpanded = new Set(expandedCategories);
        if (newExpanded.has(category)) {
            newExpanded.delete(category);
        } else {
            newExpanded.add(category);
        }
        setExpandedCategories(newExpanded);
    };

    const handlePreferenceChange = async (
        eventTypeId: string, 
        channelId: string, 
        isEnabled: boolean
    ) => {
        try {
            await notificationStore.updatePreference(eventTypeId, channelId, isEnabled);
        } catch (error) {
            console.error('Failed to update preference:', error);
        }
    };

    const handleToggleAllCategory = async (category: string, isEnabled: boolean) => {
        try {
            await notificationStore.toggleAllForCategory(category, isEnabled);
        } catch (error) {
            console.error('Failed to toggle category:', error);
        }
    };

    const isCategoryAllEnabled = (category: string): boolean => {
        const group = notificationStore.groupedPreferences.find(g => g.category === category);
        if (!group) return false;

        return Array.from(group.preferences.values()).every(enabled => enabled);
    };

    const isCategoryAllDisabled = (category: string): boolean => {
        const group = notificationStore.groupedPreferences.find(g => g.category === category);
        if (!group) return false;

        return Array.from(group.preferences.values()).every(enabled => !enabled);
    };

    const getCategoryColor = (category: string): any => {
    switch(category.toLowerCase()) {
            case 'social': return 'blue';
            case 'game': return 'green';
            case 'profile': return 'purple';
             case 'team': return 'teal';
            case 'teammatch': return 'orange';
            case 'teampost': return 'violet';
            case 'tournament': return 'yellow';
            case 'tournamentmatch': return 'brown';
            case 'tournamentpost': return 'olive';
            case 'tournamentreferee': return 'pink';
            default: return 'blue';
        }
    };

    const getCategoryIcon = (category: string): string => {
    switch(category.toLowerCase()) {
            case 'social': return 'users';
            case 'game': return 'game';
            case 'profile': return 'user';
            case 'team': return 'mail outline';
            case 'teammatch': return 'trophy';
            case 'teampost': return 'comments';
            case 'tournament': return 'calendar alternate outline';
            case 'tournamentmatch': return 'soccer';
            case 'tournamentpost': return 'announcement';
            case 'tournamentreferee': return 'hand spock';
            default: return 'bell';
        }
    };

    // Helper function to get translated channel name
    const getChannelName = (channel: { code: string; name: string }) => {
        const translationKey = `notificationChannels.${channel.code}`;
        const translated = t(translationKey);
        // Fallback to database name if translation doesn't exist
        return translated !== translationKey ? translated : channel.name;
    };

    // Helper function to get translated event type name
    const getEventTypeName = (eventType: { code: string; name: string }) => {
        const translationKey = `notificationEventTypes.${eventType.code}.name`;
        const translated = t(translationKey);
        // Fallback to database name if translation doesn't exist
        return translated !== translationKey ? translated : eventType.name;
    };

    // Helper function to get translated event type description
    const getEventTypeDescription = (eventType: { code: string; description?: string }) => {
        if (!eventType.description) return undefined;
        
        const translationKey = `notificationEventTypes.${eventType.code}.description`;
        const translated = t(translationKey);
        // Fallback to database description if translation doesn't exist
        return translated !== translationKey ? translated : eventType.description;
    };

    if (notificationStore.loadingInitial) {
        return (
            <Segment basic>
                <Dimmer active inverted>
                    <Loader size='large'>{t('loading.default')}</Loader>
                </Dimmer>
            </Segment>
        );
    }

    return (
        <Segment>
            <Header as="h2" icon textAlign="center">
                <Icon name="bell" circular />
                <Header.Content>
                    {t('notificationPreferences.title')}
                    <Header.Subheader>
                        {t('notificationPreferences.subtitle')}
                    </Header.Subheader>
                </Header.Content>
            </Header>

            <Divider />

            <Message info icon>
                <Icon name='info circle' />
                <Message.Content>
                    <Message.Header>{t('notificationPreferences.infoHeader')}</Message.Header>
                    <p>{t('notificationPreferences.infoText')}</p>
                </Message.Content>
            </Message>

            {notificationStore.groupedPreferences.map(group => {
                const isExpanded = expandedCategories.has(group.category);
                const allEnabled = isCategoryAllEnabled(group.category);
                const allDisabled = isCategoryAllDisabled(group.category);
                const categoryColor = getCategoryColor(group.category);
                const categoryIcon = getCategoryIcon(group.category);

                return (
                    <Segment key={group.category} raised>
                        <Grid>
                            <Grid.Row verticalAlign="middle">
                                <Grid.Column computer={10} tablet={10} mobile={16}>
                                    <Header as="h3">
                                        <Icon 
                                            name={isExpanded ? 'chevron down' : 'chevron right'} 
                                            onClick={() => toggleCategory(group.category)}
                                            style={{ cursor: 'pointer' }}
                                            link
                                        />
                                        <Icon className={categoryIcon} color={categoryColor} />
                                        <Header.Content>
                                            {t(`notificationPreferences.categories.${group.category.toLowerCase()}`)}
                                            <Label 
                                                color={categoryColor} 
                                                size="mini" 
                                                style={{ marginLeft: '10px' }}
                                            >
                                                {group.eventTypes.length} {t('notificationPreferences.types')}
                                            </Label>
                                        </Header.Content>
                                    </Header>
                                </Grid.Column>
                                <Grid.Column computer={6} tablet={6} mobile={16} textAlign="right">
                                    <Button.Group size="small">
                                        <Button
                                            positive
                                            basic
                                            disabled={allEnabled || notificationStore.loading}
                                            onClick={() => handleToggleAllCategory(group.category, true)}
                                        >
                                            <Icon name="check" />
                                            {t('notificationPreferences.enableAll')}
                                        </Button>
                                        <Button.Or text="•"/>
                                        <Button
                                            negative
                                            basic
                                            disabled={allDisabled || notificationStore.loading}
                                            onClick={() => handleToggleAllCategory(group.category, false)}
                                        >
                                            <Icon name="ban" />
                                            {t('notificationPreferences.disableAll')}
                                        </Button>
                                    </Button.Group>
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>

                        {isExpanded && (
                            <>
                                <Divider />
                                <Table celled structured selectable>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell width={8}>
                                                <Icon name="list" />
                                                {t('notificationPreferences.notificationType')}
                                            </Table.HeaderCell>
                                            {group.channels.map(channel => (
                                                <Table.HeaderCell key={channel.id} textAlign="center" width={2}>
                                                    <Icon name={channel.code === 'Email' ? 'mail' : 'mobile alternate'} />
                                                    {getChannelName(channel)}
                                                </Table.HeaderCell>
                                            ))}
                                        </Table.Row>
                                    </Table.Header>

                                    <Table.Body>
                                        {group.eventTypes.map(eventType => (
                                            <Table.Row key={eventType.id}>
                                                <Table.Cell>
                                                    <Header as="h5">
                                                        {getEventTypeName(eventType)}
                                                        {eventType.description && (
                                                            <Header.Subheader>
                                                                <Icon name="info circle" size="small" />
                                                                {getEventTypeDescription(eventType)}
                                                            </Header.Subheader>
                                                        )}
                                                    </Header>
                                                </Table.Cell>
                                                {group.channels.map(channel => {
                                                    const key = `${eventType.id}-${channel.id}`;
                                                    const isEnabled = group.preferences.get(key) ?? true;
                                                    
                                                    return (
                                                        <Table.Cell key={channel.id} textAlign="center">
                                                            <Checkbox
                                                                toggle
                                                                checked={isEnabled}
                                                                disabled={notificationStore.loading}
                                                                onChange={(e, data) => 
                                                                    handlePreferenceChange(
                                                                        eventType.id, 
                                                                        channel.id, 
                                                                        data.checked!
                                                                    )
                                                                }
                                                            />
                                                        </Table.Cell>
                                                    );
                                                })}
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                            </>
                        )}
                    </Segment>
                );
            })}

            {notificationStore.groupedPreferences.length === 0 && (
                <Message warning>
                    <Message.Header>{t('notificationPreferences.noPreferences')}</Message.Header>
                    <p>{t('notificationPreferences.noPreferencesText')}</p>
                </Message>
            )}
        </Segment>
    );
});

export default NotificationPreferences;